<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between mb-4" style="gap: 12px;">
      <h2 class="mb-0">Relatório Lista de Materiais</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#comercial')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <v-row class="mb-4" dense>
      <v-col cols="12" sm="6" md="4" lg="2">
        <v-text-field v-model="filtros.dataInicio" type="date" label="Solicitação inicial" solo hide-details clearable />
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="2">
        <v-text-field v-model="filtros.dataFim" type="date" label="Solicitação final" solo hide-details clearable />
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <v-autocomplete v-model="filtros.codigo" :items="opcoesOs" label="OS" solo hide-details clearable />
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <v-autocomplete v-model="filtros.job" :items="opcoesJobs" label="JOB" solo hide-details clearable />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete v-model="filtros.cliente" :items="opcoesClientes" label="Cliente" solo hide-details clearable />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete v-model="filtros.coordenador" :items="opcoesCoordenadores" label="Coordenador" solo hide-details clearable />
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-autocomplete v-model="filtros.descricao" :items="opcoesDescricoes" label="Descrição da OS" solo hide-details clearable />
      </v-col>

      <v-col cols="auto" class="d-flex align-center" style="gap: 8px;">
        <v-btn color="primary" :loading="loading" @click="buscarDados">
          <v-icon left>mdi-refresh</v-icon>
          Recarregar
        </v-btn>

        <v-menu>
          <template v-slot:activator="{ on }">
            <v-btn color="primary" outlined v-on="on" :disabled="loading || dadosFiltrados.length === 0">
              <v-icon left>mdi-file-export-outline</v-icon>
              Exportar
            </v-btn>
          </template>
          <v-list>
            <v-list-item link @click="exportarXlsxListaMateriais">
              <v-list-item-title>Excel Lista de Materiais</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="exportarPdfListaMateriais">
              <v-list-item-title>PDF Lista de Materiais</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="dadosFiltrados"
      :loading="loading"
      item-key="_id"
      rows-per-page-text="Linhas por página:"
      page-text="{0}-{1} de {2}"
      class="elevation-1"
    >
      <template v-slot:item.dataSolicitacao="{ item }">{{ formatarDataPtBr(item.dataSolicitacao) }}</template>
      <template v-slot:item.dataFabricacaoPrevista="{ item }">{{ formatarDataPtBr(item.dataFabricacaoPrevista) }}</template>
      <template v-slot:item.prazo="{ item }">{{ formatarDataPtBr(item.prazo) }}</template>
    </v-data-table>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import relatorioOSService from "@/views/Relatorios/services/relatorioOSService";
import { formatarDataPtBr } from "@/views/Relatorios/base/relatorioBaseUtils";
import UtilRelatorioOSPDF from "@/utils/UtilRelatorioOSPDF";
import relatorioPermissionMixin from "@/mixins/relatorioPermissionMixin";

export default {
  name: "RelatorioListaMaterialView",
  mixins: [relatorioPermissionMixin],
  data() {
    return {
      relatorioSchemas: ["Project"],
      relatorioFiltrarPorOwner: true,
      loading: false,
      dados: [],
      filtros: {
        dataInicio: "",
        dataFim: "",
        codigo: "",
        job: "",
        cliente: "",
        coordenador: "",
        descricao: ""
      },
      headers: [
        { text: "OS", value: "codigo", width: "80px" },
        { text: "JOB", value: "job", width: "100px" },
        { text: "Descrição", value: "descricao" },
        { text: "Cliente", value: "cliente" },
        { text: "Coordenador", value: "coordenador" },
        { text: "Dt. Solicitação", value: "dataSolicitacao", width: "110px" },
        { text: "Fabricação Prev.", value: "dataFabricacaoPrevista", width: "120px" },
        { text: "Encerramento Prev.", value: "prazo", width: "130px" }
      ]
    };
  },
  computed: {
    opcoesOs() {
      return [...new Set(this.dados.map((item) => String(item.codigo || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    opcoesJobs() {
      return [...new Set(this.dados.map((item) => String(item.job || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    opcoesClientes() {
      return [...new Set(this.dados.map((item) => String(item.cliente || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    opcoesCoordenadores() {
      return [...new Set(this.dados.map((item) => String(item.coordenador || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    opcoesDescricoes() {
      return [...new Set(this.dados.map((item) => String(item.descricao || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    dadosFiltrados() {
      const norm = (v) => String(v || "").toLowerCase().trim();
      const f = this.filtros;
      return this.dados.filter((item) => {
        if (f.codigo && !norm(item.codigo).includes(norm(f.codigo))) return false;
        if (f.job && !norm(item.job).includes(norm(f.job))) return false;
        if (f.cliente && !norm(item.cliente).includes(norm(f.cliente))) return false;
        if (f.coordenador && !norm(item.coordenador).includes(norm(f.coordenador))) return false;
        if (f.descricao && !norm(item.descricao).includes(norm(f.descricao))) return false;
        return true;
      });
    }
  },
  watch: {
    "filtros.dataInicio"() { this.buscarDados(); },
    "filtros.dataFim"() { this.buscarDados(); }
  },
  methods: {
    formatarDataPtBr,
    async buscarDados() {
      this.loading = true;
      try {
        const lista = await relatorioOSService.listarRelatorioOS(this.filtros);
        this.dados = Array.isArray(lista) ? lista : [];
      } finally {
        this.loading = false;
      }
    },
    async exportarPdfListaMateriais() {
      if (!this.dadosFiltrados.length) return;

      this.loading = true;
      try {
        const payloads = this.dadosFiltrados.map((item) => item._rawLinha || { id: item.osId, codigo: item.codigo });
        await UtilRelatorioOSPDF.gerarRelatorioOSLote(payloads);
      } catch (e) {
        console.error("[RelatorioListaMaterial] Erro ao gerar PDF:", e);
        this.$notfy?.error("Erro ao gerar PDF da Lista de Materiais.");
      } finally {
        this.loading = false;
      }
    },
    async exportarXlsxListaMateriais() {
      if (!this.dadosFiltrados.length) return;

      this.loading = true;
      try {
        const linhas = [];
        const cabecalhoItens = ["Seq", "Quant", "UM", "Item", "Descrição", "Dest", "Lib.CCO", "Dt.Necess.", "St", "Liber", "OS Filha"];

        for (const item of this.dadosFiltrados) {
          const payload = item._rawLinha || { id: item.osId, codigo: item.codigo };
          const dados = await UtilRelatorioOSPDF.enriquecerOS(payload);

          linhas.push([
            `OS: ${dados.codigo || "-"}    JOB: ${dados._jobCodigo || "-"}    Proposta: ${dados._salesOrderCodigo || "-"}`
          ]);
          linhas.push([
            `Cliente: ${dados._clienteNome || "-"}    Coordenador: ${dados._coordenadorNome || "-"}    Data Solicitação: ${this.formatarDataPtBr(dados.dataSolicitacao)}    Descrição: ${dados.descricao || "-"}`
          ]);
          linhas.push([]);
          linhas.push(cabecalhoItens);

          const itens = Array.isArray(dados._itens) ? dados._itens : [];
          itens.forEach((it) => {
            const d = it?._currentData || it || {};
            const desenhos = [d.desenhoFabricacao, d.desenhoMontagem]
              .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
              .map((v) => String(v).trim());

            const descricaoItem = desenhos.length > 0
              ? `${d.descricao || ""} | Desenho: ${desenhos.join(" / ")}`
              : (d.descricao || "");

            linhas.push([
              d.sequencia || "",
              Number(d.quantidade || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 }),
              d.unidadeSigla || "",
              d.itemCodigo || "",
              descricaoItem,
              d.destino || "",
              this.formatarDataPtBr(d.dataSolicitacao),
              this.formatarDataPtBr(d.dataNecessidade),
              d.status || "",
              this.formatarDataPtBr(d.dataLiberacaoCompras),
              dados._osMaeCodigo || ""
            ]);
          });

          linhas.push([]);
          linhas.push(["────────────────────────────────────────────────────────────────────────────────────────────────────────"]);
          linhas.push([]);
        }

        const ws = XLSX.utils.aoa_to_sheet(linhas);
        ws["!cols"] = [
          { wch: 7 },
          { wch: 9 },
          { wch: 7 },
          { wch: 12 },
          { wch: 62 },
          { wch: 8 },
          { wch: 12 },
          { wch: 12 },
          { wch: 6 },
          { wch: 12 },
          { wch: 10 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lista Materiais");
        XLSX.writeFile(wb, "relatorio-lista-materiais.xlsx");
      } catch (e) {
        console.error("[RelatorioListaMaterial] Erro ao gerar Excel:", e);
        this.$notfy?.error("Erro ao gerar Excel da Lista de Materiais.");
      } finally {
        this.loading = false;
      }
    }
  },
  async mounted() {
    if (this.relatorioTemPermissao(this.relatorioSchemas)) {
      await this.buscarDados();
    }
  }
};
</script>

<style scoped>
</style>
